import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { SimulationSession } from "../../../../api/graphql";
import { SimulationSessionListStore } from "../../store";
import { userDisplayId } from "../../../user/store/UserCommonStore";

import { VisibilityIcon, DeleteForeverIcon } from "../../../../icons";

export type GridExpressProps = { listStore?: SimulationSessionListStore };

export const SimulationSessionListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<SimulationSession>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: SimulationSession, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          label: langStore.deleteCommand,
          onClick: (row: SimulationSession) => store.deleteRow(row.id),
          icon: <DeleteForeverIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("delete"),
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<SimulationSession>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "startedAt") {
          return {
            ...col,
            id: "startedAt",
            accessorFn: (row: SimulationSession) =>
              row.startedAt
                ? new Date(row.startedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "completedAt") {
          return {
            ...col,
            id: "completedAt",
            accessorFn: (row: SimulationSession) =>
              row.completedAt
                ? new Date(row.completedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "triggeredBy") {
          return {
            ...col,
            id: "triggeredBy",
            accessorFn: (row: SimulationSession) =>
              row.triggeredBy ? userDisplayId(row.triggeredBy, langStore) : "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.simulationSessionCurrentPage || [];

    return (
      <div id={"ListOfSimulationSession"}>
        <div id={"StartOfListingOfSimulationSession"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  }
);
